import { z } from 'zod';

import {
  deploymentModeValues,
  companyStatusValues,
  planCodeValues,
  billingStatusValues,
  type CompanyDto,
  type DeploymentMode,
  type CompanyStatus,
  type PlanCode,
  type BillingStatus,
  type CreateCompanyRequest,
  type CreateCompanyResponse,
  type GetCompanyRequest,
  type GetCompanyResponse,
  type ListCompaniesRequest,
  type ListCompaniesResponse,
  type SetupCompanyDeploymentModeRequest,
  type SetupCompanyDeploymentModeResponse,
  type UpdateCompanyRequest,
  type UpdateCompanySystemRequest,
  type UpdateCompanyResponse,
} from './company.dto.js';

const timestampSchema = z.iso.datetime();
const timeZoneSchema = z
  .string()
  .trim()
  .min(1)
  .refine(isValidIanaTimeZone, { message: 'Invalid time zone' });

function isValidIanaTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

export const CompanySchema: z.ZodType<CompanyDto> = z.object({
  id: z.uuid(),
  name: z.string().trim().min(1),
  timezone: timeZoneSchema.optional(),
  receiverDirectoryScopeId: z.uuid().nullable().optional(),
  status: z.enum(companyStatusValues),
  deploymentMode: z.enum(deploymentModeValues),
  planCode: z.enum(planCodeValues),
  billingStatus: z.enum(billingStatusValues),
  trialExpiresAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const CreateCompanyRequestSchema: z.ZodType<CreateCompanyRequest> = z.object({
  name: z.string().trim().min(1),
  planCode: z.enum(planCodeValues),
  timezone: timeZoneSchema.optional(),
});

export const CreateCompanyResponseSchema: z.ZodType<CreateCompanyResponse> = z.object({
  company: CompanySchema,
  membershipId: z.uuid(),
});

export const UpdateCompanyRequestSchema: z.ZodType<UpdateCompanyRequest> = z
  .object({
    companyId: z.uuid(),
    name: z.string().trim().min(1).optional(),
    timezone: timeZoneSchema.nullable().optional(),
  })
  .strict()
  .refine((value) => value.name !== undefined || value.timezone !== undefined, {
    message: 'At least one field must be provided to update company',
  });

export const setupCompanyDeploymentModeValues = ['offline_only', 'hybrid'] as const;

export const SetupCompanyDeploymentModeRequestSchema: z.ZodType<SetupCompanyDeploymentModeRequest> =
  z
    .object({
      companyId: z.uuid(),
      deploymentMode: z.enum(setupCompanyDeploymentModeValues),
    })
    .strict();

export const SetupCompanyDeploymentModeResponseSchema: z.ZodType<SetupCompanyDeploymentModeResponse> =
  z.object({
    company: CompanySchema,
    mutation: z.object({
      changed: z.boolean(),
      source: z.literal('desktopSetup'),
    }),
  });

export const UpdateCompanySystemRequestSchema: z.ZodType<UpdateCompanySystemRequest> = z
  .object({
    companyId: z.uuid(),
    name: z.string().trim().min(1).optional(),
    timezone: timeZoneSchema.nullable().optional(),
    receiverDirectoryScopeId: z.uuid().nullable().optional(),
    status: z.enum(companyStatusValues).optional(),
    deploymentMode: z.enum(deploymentModeValues).optional(),
    planCode: z.enum(planCodeValues).optional(),
    billingStatus: z.enum(billingStatusValues).optional(),
    trialExpiresAt: timestampSchema.nullable().optional(),
  })
  .refine(
    (value) =>
      value.name !== undefined ||
      value.timezone !== undefined ||
      value.receiverDirectoryScopeId !== undefined ||
      value.status !== undefined ||
      value.deploymentMode !== undefined ||
      value.planCode !== undefined ||
      value.billingStatus !== undefined ||
      value.trialExpiresAt !== undefined,
    { message: 'At least one field must be provided to update company' },
  );

export const UpdateCompanyResponseSchema: z.ZodType<UpdateCompanyResponse> = z.object({
  company: CompanySchema,
});

export const GetCompanyRequestSchema: z.ZodType<GetCompanyRequest> = z.object({
  companyId: z.uuid(),
});

export const GetCompanyResponseSchema: z.ZodType<GetCompanyResponse> = z.object({
  company: CompanySchema,
});

export const ListCompaniesRequestSchema: z.ZodType<ListCompaniesRequest> = z.object({
  status: z.enum(companyStatusValues).optional(),
  deploymentMode: z.enum(deploymentModeValues).optional(),
  planCode: z.enum(planCodeValues).optional(),
  billingStatus: z.enum(billingStatusValues).optional(),
  page: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).max(200).optional(),
});

export const ListCompaniesResponseSchema: z.ZodType<ListCompaniesResponse> = z.object({
  items: z.array(CompanySchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(200),
  total: z.number().int().min(0),
});

export const CompanyStatusSchema: z.ZodType<CompanyStatus> = z.enum(companyStatusValues);
export const DeploymentModeSchema: z.ZodType<DeploymentMode> = z.enum(deploymentModeValues);
export const PlanCodeSchema: z.ZodType<PlanCode> = z.enum(planCodeValues);
export const BillingStatusSchema: z.ZodType<BillingStatus> = z.enum(billingStatusValues);
